// Reads and writes for the calendars screen (spec 03 §4.8, §5 rows 26 to 37),
// all through the generated client. Every queryFn passes its signal and every
// write carries a fresh Idempotency-Key. The screen loads on
// `planning.calendar.read` alone, so nothing here reads `GET /v1/planning/settings`
// except the facility-calendar save, which only a `planning.settings.manage`
// holder makes (§4.8, D-386 ruling 3).

import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { refreshQuery, shouldRetryRead } from "../../../../lib/query";
import { newIdempotencyKey, unwrap, unwrapVoid } from "../../../../lib/request";
import { shiftNamesByCode } from "../lib/calendar-lib";
import { planningKeys } from "./planning-queries";

type S = components["schemas"];
export type ShiftPattern = S["ShiftPattern"];
export type ShiftSegment = S["ShiftSegment"];
export type ShiftPatternCreate = S["ShiftPatternCreateRequest"];
export type ShiftPatternPatch = S["ShiftPatternPatchRequest"];
export type WorkCenterCalendar = S["WorkCenterCalendar"];
export type WorkCenterCalendarCreate = S["WorkCenterCalendarCreateRequest"];
export type CalendarException = S["CalendarException"];
export type CalendarExceptionCreate = S["CalendarExceptionCreateRequest"];
export type CalendarExceptionKind = CalendarException["kind"];
export type FacilityCalendarPreview = S["FacilityCalendarPreview"];
export type CalendarPreviewDay = S["CalendarPreviewDay"];
export type CalendarPreviewSegment = S["CalendarPreviewSegment"];
export type WorkCenter = S["WorkCenter"];
export type PlanningSettings = S["PlanningSettings"];

/** The five kinds of §2.14, in the order the screen offers them. */
export const EXCEPTION_KINDS: readonly CalendarExceptionKind[] = [
  "holiday",
  "planned_maintenance",
  "breakdown",
  "extra_shift",
  "overtime",
];

/** The four-week strip the preview reads (contracts `FACILITY_CALENDAR_PREVIEW_DEFAULT_DAYS`). */
export const PREVIEW_DAYS = 28;
/** The widest exceptions window one list reads (contracts `CALENDAR_EXCEPTION_WINDOW_MAX_DAYS`). */
export const EXCEPTION_WINDOW_MAX_DAYS = 731;
/** The most segments one pattern carries (contracts `SHIFT_PATTERN_MAX_SEGMENTS`). */
export const PATTERN_MAX_SEGMENTS = 42;
/** The page size the screen's tables read. */
export const CALENDAR_PAGE = 50;
/** A list read in full stops after this many pages, so a runaway cursor ends. */
const MAX_PAGES = 50;

const REF = { staleTime: 5 * 60_000, gcTime: 30 * 60_000, retry: shouldRetryRead } as const;

export interface ExceptionFilters {
  readonly from: string;
  readonly to: string;
  readonly kind?: CalendarExceptionKind | undefined;
  readonly workCenterId?: string | undefined;
}

export const calendarKeys = {
  all: ["planning", "calendars"] as const,
  patterns: () => ["planning", "calendars", "patterns"] as const,
  workCenters: (facilityId: string) =>
    ["planning", "calendars", "work-centers", facilityId] as const,
  assignments: (facilityId: string) =>
    ["planning", "calendars", "assignments", facilityId] as const,
  assignmentsPage: (
    facilityId: string,
    workCenterId: string | undefined,
    cursor: string | undefined,
  ) =>
    [
      "planning",
      "calendars",
      "assignments",
      facilityId,
      workCenterId ?? "all",
      cursor ?? null,
    ] as const,
  exceptions: (facilityId: string) => ["planning", "calendars", "exceptions", facilityId] as const,
  exceptionsPage: (facilityId: string, filters: ExceptionFilters, cursor: string | undefined) =>
    ["planning", "calendars", "exceptions", facilityId, filters, cursor ?? null] as const,
  preview: (facilityId: string) => ["planning", "calendars", "preview", facilityId] as const,
  shiftDefinitions: (facilityId: string) =>
    ["planning", "calendars", "shift-definitions", facilityId] as const,
  previewOf: (facilityId: string, from: string, workCenterId: string | undefined) =>
    ["planning", "calendars", "preview", facilityId, from, workCenterId ?? "facility"] as const,
};

// ---- reads -------------------------------------------------------------------

/**
 * The facility's shift names from module 04 (spec 04 §5 row 24), so a segment
 * reads "Night shift" rather than its code `NIGHT`. The read needs
 * `production.read`; a reader without it, or a failed read, gets an empty map
 * and the screen falls back to the code.
 */
export function useShiftNames(facilityId: string, enabled = true): ReadonlyMap<string, string> {
  const { i18n } = useTranslation();
  const defs = useQuery({
    ...REF,
    queryKey: calendarKeys.shiftDefinitions(facilityId),
    enabled: enabled && facilityId.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/shifts/definitions", {
          signal,
          params: { query: { facility_id: facilityId } },
        }),
        "shift definitions",
      ).items,
  });
  return useMemo(
    () => shiftNamesByCode(defs.data ?? [], i18n.language),
    [defs.data, i18n.language],
  );
}

/** Every pattern of the tenant, both statuses, every page (row 26). */
export function useShiftPatterns(enabled = true) {
  return useQuery({
    ...REF,
    queryKey: calendarKeys.patterns(),
    enabled,
    queryFn: async ({ signal }) => {
      const items: ShiftPattern[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < MAX_PAGES; page += 1) {
        const res = unwrap(
          await client.GET("/v1/shift-patterns", {
            signal,
            params: { query: { limit: 200, ...(cursor !== undefined ? { cursor } : {}) } },
          }),
          "shift patterns",
        );
        items.push(...res.items);
        if (res.next_cursor === null) break;
        cursor = res.next_cursor;
      }
      return items;
    },
  });
}

/**
 * The facility's work centres, both statuses: an assignment or an exception may
 * name one that was archived since, and its name must still read (§3.3b).
 */
export function useCalendarWorkCenters(facilityId: string) {
  return useQuery({
    ...REF,
    queryKey: calendarKeys.workCenters(facilityId),
    enabled: facilityId.length > 0,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/org/work-centers", {
          signal,
          params: { query: { facility_id: facilityId } },
        }),
        "work centres",
      ).items,
  });
}

/** One page of the facility's assignments (row 29). */
export function useAssignments(
  facilityId: string,
  workCenterId: string | undefined,
  cursor: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: calendarKeys.assignmentsPage(facilityId, workCenterId, cursor),
    enabled: enabled && facilityId.length > 0,
    retry: shouldRetryRead,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/work-center-calendars", {
          signal,
          params: {
            query: {
              facility_id: facilityId,
              limit: CALENDAR_PAGE,
              ...(workCenterId !== undefined ? { work_center_id: workCenterId } : {}),
              ...(cursor !== undefined ? { cursor } : {}),
            },
          },
        }),
        "work-centre calendars",
      ),
  });
}

/** One page of the facility's exceptions in a window (row 33). */
export function useCalendarExceptions(
  facilityId: string,
  filters: ExceptionFilters,
  cursor: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: calendarKeys.exceptionsPage(facilityId, filters, cursor),
    enabled: enabled && facilityId.length > 0,
    retry: shouldRetryRead,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/calendar-exceptions", {
          signal,
          params: {
            query: {
              facility_id: facilityId,
              from: filters.from,
              to: filters.to,
              limit: CALENDAR_PAGE,
              ...(filters.kind !== undefined ? { kind: filters.kind } : {}),
              ...(filters.workCenterId !== undefined
                ? { work_center_id: filters.workCenterId }
                : {}),
              ...(cursor !== undefined ? { cursor } : {}),
            },
          },
        }),
        "calendar exceptions",
      ),
  });
}

/** The four-week strip and the facility's own pattern (row 36). */
export function useCalendarPreview(
  facilityId: string,
  from: string,
  workCenterId: string | undefined,
) {
  return useQuery({
    queryKey: calendarKeys.previewOf(facilityId, from, workCenterId),
    enabled: facilityId.length > 0 && from.length > 0,
    retry: shouldRetryRead,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/facility-calendar/preview", {
          signal,
          params: {
            query: {
              facility_id: facilityId,
              from,
              days: PREVIEW_DAYS,
              ...(workCenterId !== undefined ? { work_center_id: workCenterId } : {}),
            },
          },
        }),
        "calendar preview",
      ),
  });
}

// ---- writes ------------------------------------------------------------------

export async function createShiftPattern(body: ShiftPatternCreate) {
  return unwrap(
    await client.POST("/v1/shift-patterns", {
      params: { header: { "Idempotency-Key": newIdempotencyKey() } },
      body,
    }),
    "shift pattern",
  );
}

export async function patchShiftPattern(id: string, body: ShiftPatternPatch) {
  return unwrap(
    await client.PATCH("/v1/shift-patterns/{id}", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body,
    }),
    "shift pattern",
  );
}

export async function createAssignment(body: WorkCenterCalendarCreate) {
  return unwrap(
    await client.POST("/v1/work-center-calendars", {
      params: { header: { "Idempotency-Key": newIdempotencyKey() } },
      body,
    }),
    "work-centre calendar",
  );
}

/** Sets the assignment's last date; null reopens it (row 31). */
export async function patchAssignment(id: string, validTo: string | null) {
  return unwrap(
    await client.PATCH("/v1/work-center-calendars/{id}", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: { valid_to: validTo },
    }),
    "work-centre calendar",
  );
}

export async function deleteAssignment(id: string) {
  unwrapVoid(
    await client.DELETE("/v1/work-center-calendars/{id}", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
    }),
    "work-centre calendar",
  );
}

export async function createCalendarException(body: CalendarExceptionCreate) {
  return unwrap(
    await client.POST("/v1/calendar-exceptions", {
      params: { header: { "Idempotency-Key": newIdempotencyKey() } },
      body,
    }),
    "calendar exception",
  );
}

export async function deleteCalendarException(id: string) {
  unwrapVoid(
    await client.DELETE("/v1/calendar-exceptions/{id}", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
    }),
    "calendar exception",
  );
}

/**
 * Points the facility's calendar at a pattern, or clears it with null (row 37).
 * The PUT carries the whole editable row, so this reads the row first and sends
 * it back unchanged beside the new pattern, naming the `updated_at` it read. The
 * two MPS bars go back as they are, so the lineage rule never meets this write.
 */
export async function putFacilityCalendar(
  facilityId: string,
  patternId: string | null,
): Promise<PlanningSettings> {
  const row = unwrap(
    await client.GET("/v1/planning/settings", {
      params: { query: { facility_id: facilityId } },
    }),
    "planning settings",
  );
  return unwrap(
    await client.PUT("/v1/planning/settings", {
      params: {
        query: { facility_id: facilityId },
        header: { "Idempotency-Key": newIdempotencyKey() },
      },
      body: {
        mps_auto_approve_delta_pct: row.mps_auto_approve_delta_pct,
        mps_facility_admin_delta_pct: row.mps_facility_admin_delta_pct,
        ctp_safety_margin_days: row.ctp_safety_margin_days,
        promise_ontime_downgrade_pct: row.promise_ontime_downgrade_pct,
        horizon_weeks: row.horizon_weeks,
        demand_refresh_debounce_minutes: row.demand_refresh_debounce_minutes,
        facility_shift_pattern_id: patternId,
        expected_updated_at: row.updated_at,
      },
    }),
    "planning settings",
  );
}

/** Refetches what a calendar write can move. */
export function useCalendarRefresh() {
  const qc = useQueryClient();
  return {
    all: () => refreshQuery(qc, calendarKeys.all),
    patterns: () => refreshQuery(qc, calendarKeys.patterns()),
    assignments: (facilityId: string) => refreshQuery(qc, calendarKeys.assignments(facilityId)),
    exceptions: (facilityId: string) => refreshQuery(qc, calendarKeys.exceptions(facilityId)),
    preview: (facilityId: string) => refreshQuery(qc, calendarKeys.preview(facilityId)),
    settings: (facilityId: string) => refreshQuery(qc, planningKeys.settings(facilityId)),
  };
}
